import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Loader2, ShieldCheck, MailCheck, ArrowRight, HelpCircle, KeyRound, Zap, Fingerprint } from "lucide-react";
import { isPasskeySupported, isPasskeyAutofillSupported, loginWithPasskey, loginWithPasskeyAutofill } from "@/lib/passkeys";

/**
 * 🆕 09/07/2026 — Area Clienti Login (Magic Link via Codice Fiscale)
 * Percorso: /portale-clienti (con alias /area-clienti per compatibilità)
 * Design: intenzionalmente diverso dall'admin /login (soft, informale, cliente-first).
 */
export default function AreaClientiLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuth() || {};
  const magicToken = searchParams.get("token");

  const [cf, setCf] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [consuming, setConsuming] = useState(!!magicToken);
  const [error, setError] = useState("");

  // 🆕 09/07/2026 — Tab switcher + Option B (email/password)
  const [mode, setMode] = useState("magic"); // "magic" | "classic"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pwdSubmitting, setPwdSubmitting] = useState(false);

  const [agency, setAgency] = useState({ name: "Area Riservata", phone: "", whatsapp_url: null, brand_color: "#2563EB", logo_url: null });

  // Carica info agenzia (nome, telefono, logo, colore) per personalizzare la pagina
  useEffect(() => {
    api.get("/public/client-portal/agency-info")
      .then(({ data }) => setAgency((a) => ({ ...a, ...(data || {}) })))
      .catch(() => { /* fallback silente */ });
  }, []);

  // Se arrivo con ?token=... → consumo il magic link e reindirizzo
  useEffect(() => {
    if (!magicToken) return;
    (async () => {
      try {
        const { data } = await api.post("/public/client-portal/consume-magic-link", { token: magicToken });
        if (data?.access_token) localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("session_started_at", String(Date.now()));
        toast.success("Accesso effettuato — benvenuto nella tua Area Riservata!");
        if (refresh) await refresh();
        navigate("/area-riservata", { replace: true });
      } catch (e) {
        setConsuming(false);
        setError(formatApiError(e) || "Link non valido o scaduto");
      }
    })();
  }, [magicToken]);

  const cleanCf = useMemo(() => cf.trim().toUpperCase().replace(/\s+/g, ""), [cf]);
  const cfValid = cleanCf.length === 16 || cleanCf.length === 11;

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!cfValid) {
      toast.error("Inserisci un Codice Fiscale (16 caratteri) o P.IVA (11 cifre) valido");
      return;
    }
    setSending(true);
    setError("");
    try {
      await api.post("/public/client-portal/request-magic-link", { fiscal_code: cleanCf });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err) || "Errore durante l'invio del link");
    } finally {
      setSending(false);
    }
  }, [cleanCf, cfValid]);

  // 🆕 09/07/2026 — Login classico con email + password (solo ruolo cliente)
  const submitClassic = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !password) {
      toast.error("Inserisci email e password");
      return;
    }
    setPwdSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      const role = data?.user?.role || data?.role;
      if (role !== "cliente") {
        setError("Questo account non è un account cliente. Se sei un operatore, usa /gestione-ufficio.");
        setPwdSubmitting(false);
        return;
      }
      if (data?.access_token || data?.token) {
        localStorage.setItem("access_token", data.access_token || data.token);
      }
      localStorage.setItem("session_started_at", String(Date.now()));
      toast.success("Accesso riuscito");
      if (refresh) await refresh();
      navigate("/area-riservata", { replace: true });
    } catch (err) {
      setError(formatApiError(err) || "Email o password non validi");
    } finally {
      setPwdSubmitting(false);
    }
  }, [email, password, refresh, navigate]);

  // 🆕 Feb 2026 — Login biometrico con passkey (WebAuthn)
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeySupported] = useState(() => isPasskeySupported());
  const submitPasskey = useCallback(async () => {
    if (!email.trim()) {
      setError("Inserisci l'email associata al tuo account per usare l'accesso biometrico.");
      return;
    }
    setPasskeyBusy(true);
    setError("");
    try {
      const data = await loginWithPasskey(email.trim());
      if (data?.access_token || data?.token) {
        localStorage.setItem("access_token", data.access_token || data.token);
      }
      localStorage.setItem("session_started_at", String(Date.now()));
      toast.success("Accesso biometrico riuscito");
      if (refresh) await refresh();
      navigate("/area-riservata", { replace: true });
    } catch (err) {
      // Se non ci sono passkey per questa email, suggerisci il fallback
      const detail = err?.response?.data?.detail || err?.message;
      if (String(detail || "").toLowerCase().includes("passkey")) {
        setError("Nessuna passkey attiva per questa email. Usa il link o le credenziali per accedere.");
      } else {
        setError(detail || "Accesso biometrico non riuscito");
      }
    } finally {
      setPasskeyBusy(false);
    }
  }, [email, refresh, navigate]);

  // 🆕 Feb 2026 — Conditional UI (autofill): al mount, se supportato, chiamiamo
  // startAuthentication con useBrowserAutofill: quando l'utente tocca il campo email
  // il browser propone la passkey. La promise resta appesa fino a scelta/abort.
  useEffect(() => {
    if (!passkeySupported || mode !== "classic" || sent || consuming) return;
    let cancelled = false;
    (async () => {
      const supportsAutofill = await isPasskeyAutofillSupported();
      if (!supportsAutofill || cancelled) return;
      try {
        const data = await loginWithPasskeyAutofill();
        if (cancelled) return;
        if (data?.access_token || data?.token) {
          localStorage.setItem("access_token", data.access_token || data.token);
        }
        localStorage.setItem("session_started_at", String(Date.now()));
        toast.success("Accesso biometrico riuscito");
        if (refresh) await refresh();
        navigate("/area-riservata", { replace: true });
      } catch (err) {
        // NotAllowedError / autofill_cancelled → utente non ha scelto passkey: silenzia.
        if (!err?.silent && !cancelled) {
          // Errori seri (es. verify fallito): mostra messaggio ma non blocca il login classico
          const detail = err?.response?.data?.detail || err?.message;
          if (detail && !String(detail).toLowerCase().includes("autofill")) {
            setError(detail);
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [passkeySupported, mode, sent, consuming, refresh, navigate]);

  // Compute WhatsApp URL — includes greeting text
  const whatsappHref = useMemo(() => {
    if (!agency.whatsapp_url) return null;
    const msg = encodeURIComponent(
      `Ciao! Ho bisogno di aiuto per accedere alla mia Area Riservata (${agency.name || "Agenzia"}).`
    );
    return `${agency.whatsapp_url}?text=${msg}`;
  }, [agency.whatsapp_url, agency.name]);

  // Loading state while consuming magic link
  if (consuming) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-100 via-white to-emerald-100 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md text-center border border-slate-100">
          <Loader2 className="h-10 w-10 animate-spin text-sky-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-slate-800 mb-1">Verifica in corso…</h1>
          <p className="text-slate-500 text-sm">Ti sto autenticando, attendi qualche secondo.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{
      background: `linear-gradient(135deg, ${agency.brand_color}12 0%, #F0FDF4 50%, #EFF6FF 100%)`,
    }} data-testid="area-clienti-login">
      {/* Decorative blobs */}
      <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-30 blur-3xl" style={{ background: agency.brand_color }} />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-emerald-300 rounded-full opacity-25 blur-3xl" />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          {/* Logo / titolo */}
          <div className="text-center mb-6">
            {agency.logo_url ? (
              <img src={agency.logo_url} alt={agency.name} className="h-16 mx-auto mb-3 rounded-xl shadow-sm bg-white p-2" />
            ) : (
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-white shadow-sm mb-3">
                <ShieldCheck className="h-9 w-9" style={{ color: agency.brand_color }} />
              </div>
            )}
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Area Riservata Clienti</h1>
            <p className="text-slate-500 mt-1 text-sm">{agency.name}</p>
          </div>

          {/* Card principale */}
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white p-6 sm:p-8">
            {/* 🆕 Tab switcher (nascosto se già inviato o durante consume) */}
            {!sent && (
              <div className="flex gap-2 p-1 bg-slate-100 rounded-xl mb-5" role="tablist" data-testid="login-mode-tabs">
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "magic"}
                  onClick={() => { setMode("magic"); setError(""); }}
                  className={`flex-1 py-2 px-2 rounded-lg text-xs sm:text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-all ${mode === "magic" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  data-testid="tab-magic"
                >
                  <Zap className="h-3.5 w-3.5" /> Accesso rapido
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "classic"}
                  onClick={() => { setMode("classic"); setError(""); }}
                  className={`flex-1 py-2 px-2 rounded-lg text-xs sm:text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-all ${mode === "classic" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  data-testid="tab-classic"
                >
                  <KeyRound className="h-3.5 w-3.5" /> Email e password
                </button>
              </div>
            )}

            {!sent && mode === "magic" && (
              <form onSubmit={submit} className="space-y-5" data-testid="magic-link-form">
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                    <Sparkles className="h-3 w-3" /> Accesso senza password
                  </div>
                  <h2 className="text-xl font-semibold text-slate-800 mt-3">Inserisci il tuo Codice Fiscale</h2>
                  <p className="text-slate-500 text-sm mt-1">Ti invieremo un link magico via email per accedere direttamente, senza dover ricordare alcuna password.</p>
                </div>

                <div>
                  <label htmlFor="cf-input" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                    Codice Fiscale <span className="text-slate-400 font-normal normal-case">(o Partita IVA)</span>
                  </label>
                  <input
                    id="cf-input"
                    type="text"
                    inputMode="text"
                    autoComplete="off"
                    spellCheck={false}
                    value={cf}
                    onChange={(e) => setCf(e.target.value)}
                    placeholder="RSSMRA80A01H501Z"
                    maxLength={16}
                    className="w-full h-14 px-4 border-2 border-slate-200 rounded-xl text-lg font-mono tracking-wider uppercase text-slate-800 bg-white focus:outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-100 transition"
                    data-testid="cf-input"
                    autoFocus
                  />
                  <p className="text-[11px] text-slate-400 mt-1.5 text-center">
                    {cleanCf.length > 0 && (cfValid
                      ? <span className="text-emerald-600">✓ Codice valido</span>
                      : <span>Caratteri: {cleanCf.length}/16</span>
                    )}
                  </p>
                </div>

                {error && (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2 text-sm text-center" data-testid="magic-link-error">{error}</div>
                )}

                <button
                  type="submit"
                  disabled={sending || !cfValid}
                  className="w-full h-14 rounded-xl text-white font-semibold text-base tracking-wide inline-flex items-center justify-center gap-2 transition-transform shadow-md hover:shadow-lg active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ background: `linear-gradient(135deg, ${agency.brand_color} 0%, #0EA5E9 100%)` }}
                  data-testid="magic-link-submit"
                >
                  {sending ? (<><Loader2 className="h-5 w-5 animate-spin" /> Invio in corso…</>) : (<>Ricevi il link di accesso <ArrowRight className="h-5 w-5" /></>)}
                </button>

                <p className="text-[11px] text-slate-400 text-center pt-2">
                  Riceverai un email con un link valido 20 minuti. Non hai ricevuto nulla? Controlla lo spam o contattaci.
                </p>
              </form>
            )}

            {!sent && mode === "classic" && (
              <form onSubmit={submitClassic} className="space-y-4" data-testid="classic-login-form">
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-200 rounded-full px-3 py-1">
                    <KeyRound className="h-3 w-3" /> Accesso con email e password
                  </div>
                  <h2 className="text-xl font-semibold text-slate-800 mt-3">Bentornato</h2>
                  <p className="text-slate-500 text-sm mt-1">Inserisci le credenziali della tua Area Riservata.</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="mario.rossi@email.it"
                    className="w-full h-12 px-4 border-2 border-slate-200 rounded-xl text-base text-slate-800 bg-white focus:outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-100 transition"
                    autoComplete="username webauthn"
                    data-testid="classic-email"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-12 px-4 border-2 border-slate-200 rounded-xl text-base text-slate-800 bg-white focus:outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-100 transition"
                    autoComplete="current-password"
                    data-testid="classic-password"
                  />
                </div>
                {error && (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2 text-sm text-center" data-testid="classic-login-error">{error}</div>
                )}
                <button
                  type="submit"
                  disabled={pwdSubmitting || !email || !password}
                  className="w-full h-12 rounded-xl text-white font-semibold text-base tracking-wide inline-flex items-center justify-center gap-2 transition-transform shadow-md hover:shadow-lg active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ background: `linear-gradient(135deg, ${agency.brand_color} 0%, #0EA5E9 100%)` }}
                  data-testid="classic-submit"
                >
                  {pwdSubmitting ? (<><Loader2 className="h-5 w-5 animate-spin" /> Accesso…</>) : (<>Accedi <ArrowRight className="h-5 w-5" /></>)}
                </button>
                {/* 🆕 Feb 2026 — Alternativa biometria (visibile solo se supportata) */}
                {passkeySupported && (
                  <button
                    type="button"
                    disabled={passkeyBusy || !email}
                    onClick={submitPasskey}
                    className="w-full h-11 rounded-xl border-2 border-slate-200 text-slate-700 font-semibold text-sm inline-flex items-center justify-center gap-2 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="passkey-login-btn"
                    title={email ? "Accedi con impronta / Face ID / Windows Hello" : "Inserisci l'email per usare l'accesso biometrico"}
                  >
                    {passkeyBusy ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Attesa conferma…</>
                    ) : (
                      <><Fingerprint className="h-4 w-4" /> Accedi con biometria</>
                    )}
                  </button>
                )}
                <p className="text-[11px] text-slate-400 text-center pt-2">
                  Password dimenticata? Usa il pulsante &quot;Accesso rapido&quot; qui sopra: ti invieremo un link di accesso senza password.
                </p>
              </form>
            )}
            {sent && (
              <div className="text-center py-6 space-y-4" data-testid="magic-link-sent">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <MailCheck className="h-9 w-9" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-800">Controlla la tua email</h2>
                  <p className="text-slate-500 text-sm mt-2 max-w-sm mx-auto">
                    Se il Codice Fiscale <strong className="text-slate-800 font-mono">{cleanCf}</strong> corrisponde a un cliente con Area Riservata attiva, riceverai a breve un link diretto di accesso.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setSent(false); setCf(""); setError(""); }}
                  className="text-sky-600 hover:text-sky-700 font-medium text-sm inline-flex items-center gap-1"
                  data-testid="magic-link-retry"
                >
                  ← Riprova con un altro Codice Fiscale
                </button>
              </div>
            )}
          </div>

          {/* Info sicurezza */}
          <div className="mt-6 flex items-center justify-center gap-2 text-[11px] text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5" />
            Nessuna password richiesta — accesso sicuro tramite link temporaneo.
          </div>

          {/* Divisore */}
          <div className="my-6 flex items-center gap-3 text-slate-400 text-xs">
            <div className="flex-1 h-px bg-slate-200" />
            Sei un operatore dell&apos;agenzia?
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <div className="text-center">
            <a href="/gestione-ufficio" className="text-slate-500 hover:text-sky-700 text-sm underline decoration-dotted" data-testid="operator-login-link">
              Accedi al pannello di gestione
            </a>
          </div>

          {/* 🆕 09/07/2026 — Footer branded */}
          <footer className="mt-8 text-center text-[11px] text-slate-400 space-y-1" data-testid="portale-footer">
            <div>
              © {new Date().getFullYear()} <span className="font-semibold text-slate-500">{agency.name || "Agenzia"}</span>
              {agency.email && <> · <a href={`mailto:${agency.email}`} className="hover:text-sky-600 underline decoration-dotted">{agency.email}</a></>}
              {agency.phone && <> · <a href={`tel:${agency.phone}`} className="hover:text-sky-600">{agency.phone}</a></>}
            </div>
            <div className="text-slate-400">
              Trattamento dati secondo GDPR 2016/679. Powered by <span className="font-semibold">AssicuraCRM</span>
            </div>
          </footer>
        </div>
      </div>

      {/* Floating WhatsApp help button */}
      {whatsappHref && (
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white shadow-2xl rounded-full pl-3 pr-4 py-3 font-semibold text-sm transition-transform hover:scale-105"
          title={`Contatta ${agency.name} su WhatsApp`}
          data-testid="whatsapp-help-btn"
        >
          <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-white/20 text-lg">💬</span>
          <span className="hidden sm:inline">Serve aiuto? WhatsApp</span>
          <span className="sm:hidden">Aiuto</span>
        </a>
      )}
      {!whatsappHref && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className="bg-slate-800/90 text-slate-100 rounded-full px-4 py-2 text-xs inline-flex items-center gap-2" title="Configura il telefono nelle impostazioni agenzia per abilitare il WhatsApp help">
            <HelpCircle className="h-4 w-4" /> Assistenza non disponibile
          </div>
        </div>
      )}
    </div>
  );
}
